import React, { useEffect, useRef, useState } from "react";
import {
    SovosBox,
    SovosDivider,
    SovosIconButton,
    SovosPopover,
    SovosTextField,
    SovosTypography,
} from "mosaic-react";
import {
    KeyboardArrowDown,
    Search,
    Undo,
} from "mosaic-react-icons";
import { DO_NOT_IMPORT } from "../constants";
import AiConfidencePill from "./AiConfidencePill";
import ColumnMappingAiSuggestionPopover from "./ColumnMappingAiSuggestionPopover";
import DoNotImportIcon from "./DoNotImportIcon";
import FieldTypeIcon from "./FieldTypeIcon";
import { toFieldDisplayLabel } from "../../mappingDisplayLabels";

const DropdownItem = ({
    isDoNotImportOption = false,
    label,
    type,
    disabled,
    selected,
    onClick,
}) => (
    <SovosBox
        component="button"
        onClick={onClick}
        sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            px: 1.5,
            py: 0.875,
            border: "none",
            bgcolor: selected ? "primary.50" : "transparent",
            cursor: disabled ? "default" : "pointer",
            opacity: disabled ? 0.4 : 1,
            fontFamily: "inherit",
            "&:hover": !disabled ? { bgcolor: selected ? "primary.50" : "action.hover" } : {},
        }}
    >
        {type ? (
            <FieldTypeIcon type={type} size={14} />
        ) : isDoNotImportOption ? (
            <DoNotImportIcon size={14} />
        ) : (
            <SovosBox sx={{ width: 14 }} />
        )}
        <SovosTypography
            variant="caption"
            color={selected ? "primary.main" : "text.primary"}
            fontWeight={selected ? 600 : 400}
            sx={{ flex: 1, textAlign: "left" }}
            noWrap
        >
            {label}
        </SovosTypography>
    </SovosBox>
);

const MappingDropdown = ({
    csvColumn,
    value,
    originalValue,
    mschFields,
    usedFields,
    aiScore,
    suggestionMeta,
    onChange,
    onRevert,
    entityLabel,
    onChangeEntity,
    reviewSignal = 0,
    i18n = (strings) => (Array.isArray(strings) ? strings.join("") : strings),
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [search, setSearch] = useState("");
    const [suggestionAnchorEl, setSuggestionAnchorEl] = useState(null);
    const pillRef = useRef(null);

    const open = Boolean(anchorEl);
    const suggestionOpen = Boolean(suggestionAnchorEl);

    const selectedField = value === DO_NOT_IMPORT
        ? { name: i18n`Do not import`, type: null }
        : mschFields.find((f) => f.id === value);

    const filteredFields = mschFields.filter(
        (field) => search === "" || field.name.toLowerCase().includes(search.toLowerCase()),
    );

    const handleOpen = (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
        setSearch("");
    };

    const handleSelect = (fieldId) => {
        onChange(csvColumn, fieldId);
        handleClose();
    };

    const isUsed = (fieldId) => usedFields.includes(fieldId) && value !== fieldId;

    const isMapped = !!value;
    const isDoNotImport = value === DO_NOT_IMPORT;
    const isUnmapped = !isMapped;
    const buttonLabel = selectedField ? selectedField.name : i18n`Map to`;
    const isDirty = value !== originalValue;
    const showConfidencePill = aiScore != null && !isDoNotImport;
    const targetLabel = toFieldDisplayLabel(
        selectedField?.id || value,
        mschFields,
        selectedField?.name || suggestionMeta?.displayName,
    ) || buttonLabel;

    const openSuggestionDetails = (event) => {
        event?.stopPropagation?.();
        setSuggestionAnchorEl(pillRef.current);
    };

    const closeSuggestionDetails = () => {
        setSuggestionAnchorEl(null);
    };

    useEffect(() => {
        if (reviewSignal > 0 && suggestionMeta && pillRef.current) {
            setSuggestionAnchorEl(pillRef.current);
        }
    }, [reviewSignal, suggestionMeta]);

    return (
        <>
            <SovosBox sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
                <SovosBox
                    component="button"
                    onClick={handleOpen}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: isUnmapped ? 0 : 1,
                        py: 0.375,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor:
                            isDoNotImport
                                ? "#D7DEE8"
                                : isMapped
                                  ? "#D7DEE8"
                                  : "transparent",
                        bgcolor: isDoNotImport ? "#F7F9FC" : isMapped ? "#F1F4F8" : "transparent",
                        cursor: "pointer",
                        flex: 1,
                        height: 28,
                        minWidth: 0,
                        fontFamily: "inherit",
                        "&:hover": {
                            bgcolor: isUnmapped ? "transparent" : "#EAF0F6",
                            borderColor: isUnmapped ? "transparent" : "#B8C4D2",
                        },
                    }}
                >
                    {isDoNotImport ? (
                        <DoNotImportIcon size={13} />
                    ) : (
                        isMapped && selectedField && <FieldTypeIcon type={selectedField.type} size={13} />
                    )}
                    <SovosTypography
                        variant="caption"
                        color={isUnmapped ? "text.disabled" : "#24364B"}
                        sx={{ flex: 1, textAlign: "left", fontWeight: isMapped ? 600 : 400 }}
                        noWrap
                        title={buttonLabel}
                    >
                        {buttonLabel}
                    </SovosTypography>
                    {showConfidencePill && (
                        <span
                            ref={pillRef}
                            onBlur={suggestionMeta ? closeSuggestionDetails : undefined}
                            onClick={suggestionMeta ? openSuggestionDetails : undefined}
                            onFocus={suggestionMeta ? openSuggestionDetails : undefined}
                            onMouseEnter={suggestionMeta ? openSuggestionDetails : undefined}
                            onMouseOver={suggestionMeta ? openSuggestionDetails : undefined}
                            onPointerEnter={suggestionMeta ? openSuggestionDetails : undefined}
                            style={{
                                cursor: suggestionMeta ? "pointer" : "default",
                                display: "inline-flex",
                            }}
                            tabIndex={suggestionMeta ? 0 : undefined}
                        >
                            <AiConfidencePill score={aiScore} />
                        </span>
                    )}
                    <KeyboardArrowDown sx={{ fontSize: 16, color: "text.secondary" }} />
                </SovosBox>
                {isDirty && (
                    <SovosIconButton
                        size="small"
                        tooltipText={i18n`Revert to original`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onRevert(csvColumn);
                        }}
                        sx={{ flexShrink: 0 }}
                    >
                        <Undo sx={{ fontSize: 14 }} />
                    </SovosIconButton>
                )}
            </SovosBox>

            <ColumnMappingAiSuggestionPopover
                anchorEl={suggestionAnchorEl}
                open={suggestionOpen}
                onClose={closeSuggestionDetails}
                sourceColumn={csvColumn}
                targetLabel={targetLabel}
                score={suggestionMeta?.score ?? aiScore}
                rationale={suggestionMeta?.rationale}
                i18n={i18n}
            />

            <SovosPopover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ sx: { width: 280, overflow: "hidden" } }}
            >
                <SovosBox
                    sx={{
                        px: 1.25,
                        py: 1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                    }}
                >
                    <SovosTypography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
                        {i18n`You are mapping ${entityLabel || "entity"} data`}
                    </SovosTypography>
                    <SovosTypography
                        component="button"
                        variant="caption"
                        color="primary.main"
                        onClick={() => {
                            handleClose();
                            onChangeEntity?.();
                        }}
                        sx={{
                            border: 0,
                            bgcolor: "transparent",
                            p: 0,
                            m: 0,
                            cursor: "pointer",
                            fontWeight: 600,
                            flexShrink: 0,
                        }}
                    >
                        {i18n`Change`}
                    </SovosTypography>
                </SovosBox>
                <SovosBox sx={{ p: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                    <SovosTextField
                        size="small"
                        fullWidth
                        placeholder={i18n`Search column name`}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            endAdornment: <Search sx={{ color: "text.disabled", fontSize: 16, ml: 0.5 }} />,
                        }}
                        autoFocus
                    />
                </SovosBox>

                <SovosBox sx={{ maxHeight: 260, overflowY: "auto" }}>
                    <DropdownItem
                        isDoNotImportOption
                        label={i18n`Do not import`}
                        type={null}
                        selected={value === DO_NOT_IMPORT}
                        onClick={() => handleSelect(DO_NOT_IMPORT)}
                    />
                    <SovosDivider />

                    {filteredFields.map((field) => (
                        <DropdownItem
                            key={field.id}
                            label={field.name}
                            type={field.type}
                            disabled={isUsed(field.id)}
                            selected={value === field.id}
                            onClick={() => !isUsed(field.id) && handleSelect(field.id)}
                        />
                    ))}

                    {filteredFields.length === 0 && (
                        <SovosBox sx={{ px: 2, py: 1.5 }}>
                            <SovosTypography variant="caption" color="text.disabled">
                                {i18n`No fields match`}
                            </SovosTypography>
                        </SovosBox>
                    )}
                </SovosBox>
            </SovosPopover>
        </>
    );
};

export default MappingDropdown;
